import { AppType } from "client/Api/Apps/common";
import { CalendarLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Calendar/provider";
import { ReactElement, useCallback } from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { CalendarLiveTileContent } from "./CalendarLiveTileContent";
import { TileRenderState } from "../../RenderState/common";
import { navigateToCalendarApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type CalendarTileContentProps = {
	renderState: TileRenderState<AppType.Office365Calendar>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	provider: CalendarLiveInformationProvider;
};

export const CalendarTileContent = (props: CalendarTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		provider
	} = props;

	const [navigationElement, navigateHandler] = useCalendarTileNavigation(
		renderState,
		onEdit
	);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<CalendarLiveTileContent
				provider={provider}
				onNavigate={navigateHandler}
				destinationApp={settings.destinationApp}
				destinationWindow={settings.destinationWindow}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
CalendarTileContent.displayName = "CalendarTileContent";


export function useCalendarTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Office365Calendar>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const {
		settings: {
			destinationApp,
			destinationWindow
		}
	} = renderStateIsh.data;

	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				navigateToCalendarApp(
					{
						destinationWindow,
						destinationApp
					},
					() => registerTileUsage()
				);
			},
			[
				registerTileUsage,
				destinationApp,
				destinationWindow
			]
		)
	);
}
